/**
 * Export formatters — turn a transcript (plain text + timed chunks) into the
 * three formats users actually want: plain .txt, SubRip .srt, and WebVTT .vtt.
 */
import { TranscriptChunk } from './transcribeEngine';

export type ExportFormat = 'txt' | 'srt' | 'vtt';

function pad(n: number, w = 2): string { return String(Math.floor(n)).padStart(w, '0'); }

/** seconds → "HH:MM:SS,mmm" (srt) or "HH:MM:SS.mmm" (vtt). */
function stamp(totalSec: number, sep: ',' | '.'): string {
  const s = Math.max(0, totalSec || 0);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const secs = Math.floor(s);
  return `${pad(secs / 3600)}:${pad((secs % 3600) / 60)}:${pad(secs % 60)}${sep}${pad(ms, 3)}`;
}

/** Coalesce chunks with valid start/end so a caption cue always has a duration. */
function usableCues(chunks: TranscriptChunk[]): { start: number; end: number; text: string }[] {
  const cues: { start: number; end: number; text: string }[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i];
    const text = (c.text || '').trim();
    if (!text) continue;
    const start = c.timestamp?.[0] ?? (cues.length ? cues[cues.length - 1].end : 0);
    let end = c.timestamp?.[1] ?? null;
    if (end == null) {
      const next = chunks[i + 1];
      end = next?.timestamp?.[0] ?? start + 2; // fall back to a 2 s cue
    }
    if (end <= start) end = start + 1;
    cues.push({ start, end, text });
  }
  return cues;
}

export function toSrt(chunks: TranscriptChunk[]): string {
  const cues = usableCues(chunks);
  return cues
    .map((c, i) => `${i + 1}\n${stamp(c.start, ',')} --> ${stamp(c.end, ',')}\n${c.text}\n`)
    .join('\n');
}

export function toVtt(chunks: TranscriptChunk[]): string {
  const cues = usableCues(chunks);
  const body = cues
    .map((c) => `${stamp(c.start, '.')} --> ${stamp(c.end, '.')}\n${c.text}`)
    .join('\n\n');
  return `WEBVTT\n\n${body}\n`;
}

/** Plain text — prefer the model's full text; fall back to joined chunks. */
export function toTxt(text: string, chunks: TranscriptChunk[]): string {
  const t = (text || '').trim();
  if (t) return t;
  return usableCues(chunks).map((c) => c.text).join(' ');
}

export function buildExport(
  format: ExportFormat,
  text: string,
  chunks: TranscriptChunk[],
): { content: string; mime: string; ext: ExportFormat } {
  if (format === 'srt') return { content: toSrt(chunks), mime: 'application/x-subrip', ext: 'srt' };
  if (format === 'vtt') return { content: toVtt(chunks), mime: 'text/vtt', ext: 'vtt' };
  return { content: toTxt(text, chunks), mime: 'text/plain', ext: 'txt' };
}

/** Trigger a browser download of a string as a file. */
export function downloadText(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
